<template>
  <div>
    <el-upload :action="uploadUrl" :before-upload="handleBeforeUpload" :on-success="handleUploadSuccess"
      :on-error="handleUploadError" name="file" :show-file-list="false" :headers="headers" style="display: none"
      ref="upload" v-if="this.type == 'url'">
    </el-upload>
    <div class="editor" ref="editor" :style="styles"></div>
  </div>
</template>

<script>
import axios from "axios";
import Quill from "quill";
import { StyleAttributor,Scope } from "parchment";
import "quill/dist/quill.core.css";
import "quill/dist/quill.snow.css";
import "quill/dist/quill.bubble.css";
import {
  getdeleteOssFile
} from "@/api/index";
const SizeStyle = new StyleAttributor("size","font-size",{
  scope: Scope.INLINE,
  whitelist: ["10px","13px","16px","18px","24px","32px","48px"],
});
Quill.register(SizeStyle,true);
// import { getToken } from "@/utils/auth"

export default {
  name: "Editor",
  /* 事件补充：image-remove(urls[]) — 用户操作导致文档中消失的 <img src>，urls 可多图同址出现多次需自行去重（仅 Quill.sources.USER） */
  props: {
    /* 编辑器的内容 */
    value: {
      type: String,
      default: "",
    },
    /* 高度 */
    height: {
      type: Number,
      default: null,
    },
    /* 最小高度 */
    minHeight: {
      type: Number,
      default: null,
    },
    /* 只读 */
    readOnly: {
      type: Boolean,
      default: false,
    },
    /* 上传文件大小限制(MB) */
    fileSize: {
      type: Number,
      default: 5,
    },
    /* 类型（base64格式、url格式） */
    type: {
      type: String,
      default: "url",
    },
  },
  data() {
    return {
      uploadUrl: "https://admin.hibachihomecs.com/api/upload", // 上传的图片服务器地址
      headers: {
        // Authorization: "Bearer " + getToken()
      },
      Quill: null,
      /** 当前编辑器内 `<img>` 地址列表（可含重复），用于与用户删除操作时 diff */
      trackedImgSrcList: [],
      currentValue: "",
      options: {
        theme: "snow",
        bounds: document.body,
        debug: "warn",
        modules: {
          // 工具栏配置
          toolbar: [
            ["bold","italic","underline","strike"], // 加粗 斜体 下划线 删除线
            ["blockquote","code-block"], // 引用  代码块
            [{ list: "ordered" },{ list: "bullet" }], // 有序、无序列表
            [{ indent: "-1" },{ indent: "+1" }], // 缩进
            [{ size: ["10px","13px",false,"18px","24px","32px","48px"] }], // 字体大小
            [{ header: [1,2,3,4,5,6,false] }], // 标题
            [{ color: [] },{ background: [] }], // 字体颜色、字体背景颜色
            [{ align: [] }], // 对齐方式
            ["clean"], // 清除文本格式
            ["link","image"], // 链接、图片、视频  "video"
          ],
        },
        placeholder: "请输入内容",
        readOnly: this.readOnly,
      },
    };
  },
  computed: {
    styles() {
      let style = {};
      if(this.minHeight) {
        style.minHeight = `${this.minHeight}px`;
      }
      if(this.height) {
        style.height = `${this.height}px`;
      }
      return style;
    },
  },
  watch: {
    value: {
      handler(val) {
        if(val !== this.currentValue) {
          this.currentValue = val === null ? "" : val;
          if(this.Quill) {
            this.Quill.clipboard.dangerouslyPasteHTML(this.currentValue);
            this.$nextTick(() => {
              const root = this.$refs.editor?.children?.[0];
              this.trackedImgSrcList = root
                ? this.extractImgSrcsFromHtml(root.innerHTML)
                : [];
            });
          }
        }
      },
      immediate: true,
    },
  },
  mounted() {
    this.init();
  },
  beforeDestroy() {
    this.Quill = null;
  },
  methods: {
    /** 从编辑器 HTML 中取出所有 `<img src>`（保留重复，便于多块相同图） */
    extractImgSrcsFromHtml(html) {
      if(!html || typeof html !== "string") {
        return [];
      }
      const div = document.createElement("div");
      div.innerHTML = html;
      const imgs = div.querySelectorAll("img[src]");
      const list = [];
      imgs.forEach((img) => {
        const raw = img.getAttribute("src");
        if(raw) {
          list.push(raw.trim());
        }
      });
      return list;
    },
    /** 上一轮相对新一轮少掉的 `<img>` 地址（多实例按次数逐项扣减） */
    diffRemovedImageUrls(previousUrls,nextUrls) {
      const nextRemaining = new Map();
      nextUrls.forEach((u) => {
        nextRemaining.set(u,(nextRemaining.get(u) || 0) + 1);
      });
      const removed = [];
      previousUrls.forEach((u) => {
        const left = nextRemaining.get(u) || 0;
        if(left > 0) {
          nextRemaining.set(u,left - 1);
        } else {
          removed.push(u);
        }
      });
      return removed;
    },
    init() {
      const editor = this.$refs.editor;
      this.Quill = new Quill(editor,this.options);
      // 如果设置了上传地址则自定义图片上传事件
      if(this.type == "url") {
        let toolbar = this.Quill.getModule("toolbar");
        toolbar.addHandler("image",(value) => {
          if(value) {
            this.$refs.upload.$children[0].$refs.input.click();
          } else {
            this.Quill.format("image",false);
          }
        });
        this.Quill.root.addEventListener(
          "paste",
          this.handlePasteCapture,
          true
        );
      }
      this.Quill.clipboard.dangerouslyPasteHTML(this.currentValue);
      const rootAfterPaste = this.$refs.editor.children[0];
      this.trackedImgSrcList = rootAfterPaste
        ? this.extractImgSrcsFromHtml(rootAfterPaste.innerHTML)
        : [];
      this.Quill.on("text-change",(delta,oldDelta,source) => {
        const html = this.$refs.editor.children[0].innerHTML;
        const nextImgList = this.extractImgSrcsFromHtml(html);
        if(source === Quill.sources.USER) {
          const removed = this.diffRemovedImageUrls(
            this.trackedImgSrcList,
            nextImgList
          );
          if(removed.length) {
            console.log("[Editor] image-remove",removed);
            // this.getdeleteOss(removed[0]);
            this.$emit("image-remove",removed);
          }
        }
        this.trackedImgSrcList = nextImgList;
        const text = this.Quill.getText();
        const quill = this.Quill;
        this.currentValue = html;
        this.$emit("input",html);
        this.$emit("on-change",{ html,text,quill });
      });
      this.Quill.on("text-change",(delta,oldDelta,source) => {
        this.$emit("on-text-change",delta,oldDelta,source);
      });
      this.Quill.on("selection-change",(range,oldRange,source) => {
        this.$emit("on-selection-change",range,oldRange,source);
      });
      this.Quill.on("editor-change",(eventName,...args) => {
        this.$emit("on-editor-change",eventName,...args);
      });
    },
    /** 从图片完整地址中取文件名（最后一个 / 之后的部分），不含域名与路径 */
    getdeleteOss(url) {
      if(!url || typeof url !== "string") return "";
      const noQueryHash = url.split("?")[0].split("#")[0];
      const idx = noQueryHash.lastIndexOf("/");
      let fileName = idx >= 0 ? noQueryHash.slice(idx + 1) : noQueryHash;
      if(!fileName) return;
      getdeleteOssFile({
        fileName: fileName,
      });

    },
    // 上传前校检格式和大小
    handleBeforeUpload(file) {
      const type = ["image/jpeg","image/jpg","image/png","image/svg"];
      const isJPG = type.includes(file.type);
      // 检验文件格式
      if(!isJPG) {
        this.$message.error(`图片格式错误!`);
        return false;
      }
      // 校检文件大小
      if(this.fileSize) {
        const isLt = file.size / 1024 / 1024 < this.fileSize;
        if(!isLt) {
          this.$message.error(`上传文件大小不能超过 ${this.fileSize} MB!`);
          return false;
        }
      }
      return true;
    },
    handleUploadSuccess(res,file) {
      console.log(res,"ress");

      // 如果上传成功
      if(res.code == 0) {
        // 获取富文本组件实例
        let quill = this.Quill;
        // 获取光标所在位置
        let length = quill.getSelection().index;
        // 插入图片  res.url为服务器返回的图片地址
        quill.insertEmbed(length,"image",res.url);
        // 调整光标到最后
        quill.setSelection(length + 1);
      } else {
        this.$message.error("图片插入失败");
      }
    },
    handleUploadError() {
      this.$message.error("图片插入失败");
    },
    // 复制粘贴图片处理
    handlePasteCapture(e) {
      const clipboard = e.clipboardData || window.clipboardData;
      if(clipboard && clipboard.items) {
        for(let i = 0; i < clipboard.items.length; i++) {
          const item = clipboard.items[i];
          if(item.type.indexOf("image") !== -1) {
            e.preventDefault();
            const file = item.getAsFile();
            this.insertImage(file);
          }
        }
      }
    },
    insertImage(file) {
      const formData = new FormData();
      formData.append("file",file);
      axios
        .post(this.uploadUrl,formData,{
          headers: {
            "Content-Type": "multipart/form-data",
            // Authorization: this.headers.Authorization,
          },
        })
        .then((res) => {
          this.handleUploadSuccess(res.data);
        });
    },
  },
};
</script>

<style>
.editor,
.ql-toolbar {
  white-space: pre-wrap !important;
  line-height: normal !important;
}

.quill-img {
  display: none;
}

.ql-snow .ql-tooltip[data-mode="link"]::before {
  content: "请输入链接地址:";
}

.ql-snow .ql-tooltip.ql-editing a.ql-action::after {
  border-right: 0px;
  content: "保存";
  padding-right: 0px;
}

.ql-snow .ql-tooltip[data-mode="video"]::before {
  content: "请输入视频地址:";
}

.ql-snow .ql-picker.ql-size .ql-picker-label::before,
.ql-snow .ql-picker.ql-size .ql-picker-item::before {
  content: "16px";
}

.ql-snow .ql-picker.ql-size .ql-picker-label[data-value="10px"]::before,
.ql-snow .ql-picker.ql-size .ql-picker-item[data-value="10px"]::before {
  content: "10px";
}

.ql-snow .ql-picker.ql-size .ql-picker-label[data-value="13px"]::before,
.ql-snow .ql-picker.ql-size .ql-picker-item[data-value="13px"]::before {
  content: "13px";
}

.ql-snow .ql-picker.ql-size .ql-picker-label[data-value="18px"]::before,
.ql-snow .ql-picker.ql-size .ql-picker-item[data-value="18px"]::before {
  content: "18px";
}

.ql-snow .ql-picker.ql-size .ql-picker-label[data-value="24px"]::before,
.ql-snow .ql-picker.ql-size .ql-picker-item[data-value="24px"]::before {
  content: "24px";
}

.ql-snow .ql-picker.ql-size .ql-picker-label[data-value="32px"]::before,
.ql-snow .ql-picker.ql-size .ql-picker-item[data-value="32px"]::before {
  content: "32px";
}

.ql-snow .ql-picker.ql-size .ql-picker-label[data-value="48px"]::before,
.ql-snow .ql-picker.ql-size .ql-picker-item[data-value="48px"]::before {
  content: "48px";
}

.ql-snow .ql-picker.ql-header .ql-picker-label::before,
.ql-snow .ql-picker.ql-header .ql-picker-item::before {
  content: "文本";
}

.ql-snow .ql-picker.ql-header .ql-picker-label[data-value="1"]::before,
.ql-snow .ql-picker.ql-header .ql-picker-item[data-value="1"]::before {
  content: "标题1";
}

.ql-snow .ql-picker.ql-header .ql-picker-label[data-value="2"]::before,
.ql-snow .ql-picker.ql-header .ql-picker-item[data-value="2"]::before {
  content: "标题2";
}

.ql-snow .ql-picker.ql-header .ql-picker-label[data-value="3"]::before,
.ql-snow .ql-picker.ql-header .ql-picker-item[data-value="3"]::before {
  content: "标题3";
}

.ql-snow .ql-picker.ql-header .ql-picker-label[data-value="4"]::before,
.ql-snow .ql-picker.ql-header .ql-picker-item[data-value="4"]::before {
  content: "标题4";
}

.ql-snow .ql-picker.ql-header .ql-picker-label[data-value="5"]::before,
.ql-snow .ql-picker.ql-header .ql-picker-item[data-value="5"]::before {
  content: "标题5";
}

.ql-snow .ql-picker.ql-header .ql-picker-label[data-value="6"]::before,
.ql-snow .ql-picker.ql-header .ql-picker-item[data-value="6"]::before {
  content: "标题6";
}

.ql-snow .ql-picker.ql-font .ql-picker-label::before,
.ql-snow .ql-picker.ql-font .ql-picker-item::before {
  content: "标准字体";
}

.ql-snow .ql-picker.ql-font .ql-picker-label[data-value="serif"]::before,
.ql-snow .ql-picker.ql-font .ql-picker-item[data-value="serif"]::before {
  content: "衬线字体";
}

.ql-snow .ql-picker.ql-font .ql-picker-label[data-value="monospace"]::before,
.ql-snow .ql-picker.ql-font .ql-picker-item[data-value="monospace"]::before {
  content: "等宽字体";
}
</style>
